/** How often the recorder emits a chunk. Small enough that a crash loses little. */
const TIMESLICE_MS = 1000

/** Preference order; the first the browser admits to supporting wins. */
const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']

/** @returns The best container this browser can record, or '' to let it choose. */
function pickMimeType(): string {
  return MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) ?? ''
}

/**
 * Streams a meeting's audio as a compressed container instead of raw PCM.
 *
 * What an internal meeting sends: the server runs speech-to-text over the whole
 * file after the meeting rather than live, so there is no need for the raw
 * 16 kHz frames the realtime path sends, and WebM/Opus is a fraction of the
 * bytes (BRAI-460).
 *
 * Separate from `ArchiveAudioRecorder`, which records the same audio for
 * playback: that one buffers every chunk to build one blob at the end, this one
 * hands each chunk straight to the socket and keeps nothing.
 */
export class ContainerAudioStreamer {
  private recorder: MediaRecorder | null = null
  private onChunk: ((chunk: Blob) => void) | null = null

  /**
   * @param stream Live capture to record.
   * @param onChunk Called for each container chunk, in order.
   */
  start(stream: MediaStream, onChunk: (chunk: Blob) => void): void {
    if (this.recorder && this.recorder.state !== 'inactive') return

    this.onChunk = onChunk
    const mimeType = pickMimeType()
    this.recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)

    this.recorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.onChunk?.(event.data)
    }

    this.recorder.start(TIMESLICE_MS)
  }

  isRecording(): boolean {
    return this.recorder !== null && this.recorder.state !== 'inactive'
  }

  /**
   * Stops the recorder without sending anything.
   *
   * Pausing rather than letting the stream run: the server discards audio that
   * arrives while a meeting is paused, so anything sent meanwhile becomes a hole
   * in the middle of the file rather than silence at the right place.
   */
  pause(): void {
    const recorder = this.recorder
    if (!recorder || recorder.state !== 'recording') return
    if (typeof recorder.pause !== 'function') return
    recorder.pause()
  }

  /** Resumes a {@link pause}d recording, continuing the same container. */
  resume(): void {
    const recorder = this.recorder
    if (!recorder || recorder.state !== 'paused') return
    if (typeof recorder.resume !== 'function') return
    recorder.resume()
  }

  /**
   * Ends the recording and resolves once its final chunk has been handed over.
   *
   * Awaited rather than fire-and-forget because `stop()` emits one last
   * `dataavailable` holding everything since the previous timeslice. Telling the
   * server the meeting is over before that arrives drops up to a second of the
   * ending — the part where meetings decide things.
   */
  stop(): Promise<void> {
    const recorder = this.recorder
    this.recorder = null

    if (!recorder || recorder.state === 'inactive') {
      this.onChunk = null
      return Promise.resolve()
    }

    return new Promise<void>((resolve) => {
      recorder.onstop = () => {
        this.onChunk = null
        resolve()
      }
      recorder.stop()
    })
  }
}
